import { useProject } from '@/services/projects';
import { useUser } from '@/services/user';
import { useAuthStore } from '@/store/auth';
import { eventsBasePath } from '@/utils/apiBasePath';
import { captureException } from '@sentry/vue';
import { whenever } from '@vueuse/core';
import { computed, toValue } from 'vue';
import { type EventRoute, useEventRoute } from './useEventRoute';
import { useMqtt } from './useMqtt';

type SubscriptionHandler = (payload: any, topic: EventRoute) => void;

const toMqttTopic = (topic: string) => topic.replaceAll('.', '/').replaceAll('*', '+');
const fromMqttTopic = (topic: string) => topic.replaceAll('/', '.').replaceAll('+', '*');

export const useEvents = () => {
  const authStore = useAuthStore();
  const user = useUser().active;
  const project = useProject();
  const handlers = new Map<SubscriptionHandler, (payload: Uint8Array, topic: string) => void>();

  const username = computed(() => toValue(user)?.id || authStore.subject);
  const mqtt = useMqtt(eventsBasePath(), username);

  const subscribe = (channel: string, topic: string, handler: SubscriptionHandler) => {
    const route = useEventRoute(channel, topic);
    const mqttHandler = (payload: Uint8Array, topic: string) => {
      try {
        const stringPayload = new TextDecoder().decode(payload);
        const jsonPayload = JSON.parse(stringPayload);

        handler(jsonPayload, route.parse(fromMqttTopic(topic)));
      } catch (error) {
        captureException(error);
      }
    };

    handlers.set(handler, mqttHandler);

    return mqtt.subscribe(toMqttTopic(route.topic), mqttHandler);
  };

  const unsubscribe = (channel: string, topic: string, handler?: SubscriptionHandler) => {
    const route = useEventRoute(channel, topic);
    const mqttHandler = handler ? handlers.get(handler) : undefined;

    return mqtt.unsubscribe(toMqttTopic(route.topic), mqttHandler);
  };

  const publish = (channel: string, topic: string, payload: any) => {
    const route = useEventRoute(channel, topic);

    return mqtt.publish(toMqttTopic(route.topic), JSON.stringify(payload));
  };

  return {
    subscribe,
    unsubscribe,
    publish,

    user: {
      subscribe: (topic: string, handler: (payload: any, route: EventRoute) => void) => {
        whenever(username, (username) => subscribe(`user.${username}`, topic, handler), {
          once: true,
          immediate: true,
        });
      },

      unsubscribe: (topic: string) => {
        whenever(username, (username) => unsubscribe(`user.${username}`, topic), {
          once: true,
          immediate: true,
        });
      },

      publish: (topic: string, payload: any) => {
        whenever(username, (username) => publish(`user.${username}`, topic, payload), {
          once: true,
          immediate: true,
        });
      },
    },

    project: {
      subscribe: (topic: string, handler: (payload: any, route: EventRoute) => void) => {
        whenever(project, ({ id }) => subscribe(`project.${id}`, topic, handler), {
          once: true,
          immediate: true,
        });
      },

      unsubscribe: (topic: string) => {
        whenever(project, (project) => unsubscribe(`project.${project.id}`, topic), {
          once: true,
          immediate: true,
        });
      },

      publish: (topic: string, payload: any) => {
        whenever(project, (project) => publish(`project.${project.id}`, topic, payload), {
          once: true,
          immediate: true,
        });
      },
    },
  };
};
